import { useMemo, useState } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { ClipboardList, Plus } from 'lucide-react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { buttonVariants } from '@/components/ui/button';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { cn } from '@/lib/utils';
import { surveyListInfiniteQueryOptions } from '../api/surveys.api';
import { useSurveyFilters } from '../hooks/useSurveyFilters';
import type { SurveyListItem } from '../types/survey.types';
import { getSurveyColumns } from './SurveyColumns';
import { SurveyFilterPanel } from './SurveyFilterPanel';

export function SurveyListTable() {
  const router = useRouter();
  const controller = useSurveyFilters();
  const { search, hasActiveFilters, listQueryParams } = controller;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedSurveyId = useRevealLastViewedRow('surveys');

  const {
    rows: surveys,
    firstPageMeta,
    isLoading,
    loadError,
    refetch,
    loadMoreProps,
  } = useInfiniteTableQuery(surveyListInfiniteQueryOptions(listQueryParams));

  const columns = useMemo(() => getSurveyColumns(search.q), [search.q]);
  const showEmptyOnboarding = !isLoading && !hasActiveFilters && surveys.length === 0;

  const totals = firstPageMeta?.totals;
  const stats: LabeledStat[] = [
    { key: 'total', label: 'Total', value: firstPageMeta?.total },
    { key: 'active', label: 'Active', value: totals?.active },
    { key: 'expired', label: 'Expired', value: totals?.expired },
    { key: 'responses', label: 'Responses', value: totals?.responses },
  ];

  function goToCreateSurvey() {
    void router.navigate({ to: '/feedback/surveys/new' });
  }

  function handleRowClick(row: SurveyListItem) {
    void router.navigate({
      to: '/feedback/surveys/$surveyId',
      params: { surveyId: row.id },
    });
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Surveys"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={search.from}
            dateTo={search.to}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: 'Search by title',
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
        createAction={{ label: 'Create Survey', onClick: goToCreateSurvey }}
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <SurveyFilterPanel filters={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load surveys"
          onRetry={() => void refetch()}
          fill
        />
      ) : showEmptyOnboarding ? (
        <EmptyState
          icon={ClipboardList}
          title="No surveys yet"
          description="Create your first survey to start collecting field responses."
          className="lg:flex-1"
          actions={
            <Link
              to="/feedback/surveys/new"
              className={cn(buttonVariants({ variant: 'default', size: 'sm' }), 'gap-1.5')}
            >
              <Plus className="size-3.5" />
              Create Survey
            </Link>
          }
        />
      ) : (
        <>
          <StatCardGrid stats={stats} isLoading={isLoading} />

          <DataTable
            data={surveys}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="title"
            emptyIcon={ClipboardList}
            isRowHighlighted={(survey) => survey.id === lastViewedSurveyId}
            {...loadMoreProps}
            onRowClick={handleRowClick}
            className="lg:flex-1"
          />
        </>
      )}
    </div>
  );
}
